import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';

/**
 * Radio Studio Più — global player context.
 * Keeps a single <audio> element mounted at the App root so that navigating
 * between pages doesn't stop the stream. Any component can call play/pause,
 * and the sticky mini-player watches this state.
 */

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const NOW_POLL_MS = 30000;      // refetch metadata every 30s
const SCHEDULE_POLL_MS = 300000; // refetch schedule every 5 min

const RadioPlayerCtx = createContext(null);
export const useRadioPlayer = () => useContext(RadioPlayerCtx);

export function RadioPlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [now, setNow] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolumeState] = useState(0.8);
  const [modalOpen, setModalOpen] = useState(false);
  const [miniVisible, setMiniVisible] = useState(false); // sticky mini player visible when playing
  const [error, setError] = useState('');

  // Fetch now-playing periodically
  const fetchNow = useCallback(() => {
    axios.get(`${API}/public/radio/studiopiu/now`)
      .then(r => setNow(r.data))
      .catch(() => {});
  }, []);
  const fetchSchedule = useCallback(() => {
    axios.get(`${API}/public/radio/studiopiu/schedule?next_count=3`)
      .then(r => setSchedule(r.data.upcoming || []))
      .catch(() => setSchedule([]));
  }, []);

  useEffect(() => {
    fetchNow();
    fetchSchedule();
    const i1 = setInterval(fetchNow, NOW_POLL_MS);
    const i2 = setInterval(fetchSchedule, SCHEDULE_POLL_MS);
    return () => { clearInterval(i1); clearInterval(i2); };
  }, [fetchNow, fetchSchedule]);

  // Sync volume
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const play = useCallback(() => {
    const a = audioRef.current;
    if (!a || !now?.stream?.url) return;
    if (a.src !== now.stream.url) a.src = now.stream.url;
    a.play()
      .then(() => { setPlaying(true); setMiniVisible(true); setError(''); })
      .catch(err => { console.warn('Radio play failed:', err); setError('impossibile avviare lo streaming'); });
  }, [now]);

  const pause = useCallback(() => {
    if (audioRef.current) audioRef.current.pause();
    setPlaying(false);
  }, []);

  const toggle = useCallback(() => {
    if (playing) pause(); else play();
  }, [playing, play, pause]);

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);
  const closeMini = useCallback(() => { pause(); setMiniVisible(false); }, [pause]);

  const setVolume = useCallback((v) => setVolumeState(Math.max(0, Math.min(1, v))), []);

  const value = {
    now, schedule, playing, volume, modalOpen, miniVisible, error,
    play, pause, toggle, setVolume, openModal, closeModal, closeMini,
  };

  return (
    <RadioPlayerCtx.Provider value={value}>
      {children}
      {/* Single audio element mounted at root */}
      <audio ref={audioRef} preload="none"
             onPlay={() => setPlaying(true)}
             onPause={() => setPlaying(false)}
             onError={() => { setPlaying(false); setError('errore streaming'); }}
             crossOrigin="anonymous"/>
    </RadioPlayerCtx.Provider>
  );
}


// -------- Helper: resolve remote image URL from studiopiu.net --------
export function resolveRadioImg(path) {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `https://www.studiopiu.net${path}`;
}
